import { useAuth0 } from '@auth0/auth0-react'
import { useState, useEffect } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { loginWithRedirect } = useAuth0()
  const [email, setEmail] = useState('')
  const [checking, setChecking] = useState(false)
  const [showSignup, setShowSignup] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Auto-login if we know the provider from a previous session
  useEffect(() => {
    if (!isOpen) return
    const saved = localStorage.getItem('pethalo_auth_provider')
    if (saved) {
      const connection = saved === 'google' ? 'google-oauth2' : saved === 'apple' ? 'apple' : undefined
      onClose()
      loginWithRedirect({ authorizationParams: connection ? { connection } : undefined })
    }
  }, [isOpen])

  if (!isOpen) return null

  const loginWith = (connection?: string) => {
    // Remember provider for next time
    const provider = connection === 'google-oauth2' ? 'google' : connection === 'apple' ? 'apple' : 'email'
    localStorage.setItem('pethalo_auth_provider', provider)

    onClose()
    loginWithRedirect({
      authorizationParams: connection ? { connection } : undefined,
    })
  }

  const lookupEmail = async () => {
    setChecking(true)
    setShowSignup(false)
    setError(null)

    try {
      const res = await fetch(`${API_URL}/api/users/lookup?email=${encodeURIComponent(email)}`)
      const data = await res.json()

      if (data.exists && data.provider) {
        // Go directly to auth provider — no intermediate step
        loginWith(providerConnection(data.provider))
        return
      } else {
        setShowSignup(true)
      }
    } catch {
      setError('Unable to check email. Please try again.')
    }
    setChecking(false)
  }

  const providerConnection = (p: string) => p === 'google' ? 'google-oauth2' : p === 'apple' ? 'apple' : undefined

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Welcome to Pet Halo</h2>
              <p className="text-sm text-gray-500 mt-1">Enter your email to get started</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Email input */}
          <div className="space-y-3 mb-6">
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && email && lookupEmail()}
              className="input-field"
            />
            <button
              onClick={lookupEmail}
              disabled={!email || checking}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {checking && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              {checking ? 'Checking...' : 'Continue'}
            </button>
          </div>

          {/* New user — signup options */}
          {showSignup && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">No account found. Create one:</p>

              <button
                onClick={() => loginWith('google-oauth2')}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="font-medium text-gray-700">Sign up with Google</span>
              </button>

              <button
                onClick={() => loginWith('apple')}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span className="font-medium">Sign up with Apple</span>
              </button>

              <button
                onClick={() => loginWith()}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-primary-400 text-white rounded-lg hover:bg-primary-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                <span className="font-medium">Sign up with Email</span>
              </button>
            </div>
          )}

          {error && <p className="text-sm text-red-500 mt-4">{error}</p>}

          <p className="text-xs text-gray-400 text-center mt-6">
            You'll be redirected to a secure login page
          </p>
        </div>
      </div>
    </div>
  )
}
